import { Group, WebGLRenderer } from "three";
import { setScale, setUpRotation } from "./data/coords";
import { loadManifest, loadScene } from "./data/loadScene";
import { loadPoses } from "./data/loadPoses";
import { resolveAssetUrl } from "./data/assetBase";
import { buildFreeLook } from "./cameras/freeLook";
import { buildSnapCamera } from "./cameras/snapView";
import { buildCameraRigs } from "./scene/cameraRig";
import { buildScene } from "./scene/sceneBuilder";
import { SkeletonGroups } from "./scene/skeletonGroup";
import { makeSplatBackdrop } from "./scene/splatBackdrop";
import { PoseInterpolator } from "./playback/poseInterpolator";
import { startRafLoop } from "./playback/rafLoop";
import { VideoClock } from "./playback/videoClock";
import { setupCameraPicker } from "./ui/cameraPicker";
import { setupGidPicker } from "./ui/gidPicker";
import { setupSplatTuner } from "./ui/splatTuner";
import { setupTimeline } from "./ui/timeline";
import type { ManifestVideo } from "./data/types";

const statusEl = document.getElementById("status") as HTMLDivElement;
function status(msg: string): void { statusEl.textContent = msg; }

async function findBundleBase(): Promise<string> {
  // URL ?bundle=<hash> overrides; otherwise auto-pick the first dir.
  const params = new URLSearchParams(location.search);
  const explicit = params.get("bundle");
  if (explicit) return `/viewer_data/${explicit}`;

  // Fallback: try a directory listing (Vite serves /public/* directly).
  // The exporter writes /viewer_data/<config-hash>/scene.json — we don't have
  // a directory index, so just try the known-existing bundle (which the user
  // can override via ?bundle=).
  // For dev convenience we list candidates explicitly.
  const candidates: string[] = [];
  // Hashes from recent runs, newest first. Harmless if they don't exist.
  candidates.push("/viewer_data/8bcff7ed662e");  // vru_selfcalib 700-frame run (current pipeline)
  candidates.push("/viewer_data/6a521dc7048e");
  candidates.push("/viewer_data/c9c878a3ab1b");
  candidates.push("/viewer_data/66c6a7dca0f8");
  candidates.push("/viewer_data/3de555a1e125");
  for (const base of candidates) {
    try {
      const r = await fetch(`${base}/scene.json`, { method: "HEAD" });
      if (r.ok) return base;
    } catch { /* ignore */ }
  }
  throw new Error("No viewer_data bundle found. Pass ?bundle=<hash>.");
}

async function main(): Promise<void> {
  status("loading bundle…");
  const baseUrl = await findBundleBase();
  const [sceneJson, posesJson, manifest] = await Promise.all([
    loadScene(baseUrl),
    loadPoses(baseUrl),
    loadManifest(baseUrl),
  ]);
  // Re-root relative asset URLs onto ?asset_base=<host> when set (lets a
  // deployed bundle whose manifest still has dev-relative "../long360/…" URLs
  // fetch the mp4s/splat from R2 without a re-export). No-op in dev / when the
  // export already baked an absolute host in. See data/assetBase.ts + DEPLOY.md.
  for (const v of manifest.videos) v.url = resolveAssetUrl(v.url);
  if (sceneJson.splat) sceneJson.splat.url = resolveAssetUrl(sceneJson.splat.url);
  setScale(sceneJson.metres_per_wu);
  setUpRotation(sceneJson.up_rotation);  // identity if absent (older bundles)

  status(`bundle ${sceneJson.config_hash}: ${manifest.videos.length} cams, `
       + `${posesJson.frames.length} pose frames`);

  // --- three.js scaffolding -----------------------------------------
  const canvas = document.getElementById("canvas") as HTMLCanvasElement;
  const renderer = new WebGLRenderer({
    canvas, antialias: true, alpha: true,
  });
  renderer.setClearAlpha(1.0);  // free-look = opaque dark bg
  renderer.setPixelRatio(window.devicePixelRatio);

  const videoEl = document.getElementById("backdrop") as HTMLVideoElement;

  // Hooks called whenever the canvas pixel size changes (Line2 thickness
  // shader needs the resolution; possibly other size-sensitive consumers).
  const layoutHooks: Array<(w: number, h: number) => void> = [];

  /**
   * Resize canvas (and matching <video> overlay) to either the full viewport
   * (free-look) or a centered letterbox of the camera's aspect (snap mode).
   * Returns the active aspect ratio so the perspective camera can match.
   */
  function layoutFor(aspect: number | null): number {
    const ww = window.innerWidth;
    const wh = window.innerHeight;
    let w: number, h: number, left: number, top: number;
    if (aspect == null) {
      w = ww; h = wh; left = 0; top = 0;
    } else {
      const windowAspect = ww / wh;
      if (windowAspect > aspect) {
        // window is wider than camera; letterbox left/right
        h = wh; w = Math.round(h * aspect); left = Math.round((ww - w) / 2); top = 0;
      } else {
        // window is taller than camera; letterbox top/bottom
        w = ww; h = Math.round(w / aspect); left = 0; top = Math.round((wh - h) / 2);
      }
    }
    for (const el of [canvas, videoEl]) {
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;
    }
    renderer.setSize(w, h, false);
    for (const cb of layoutHooks) cb(w, h);
    return w / h;
  }
  layoutFor(null);

  const scene = buildScene(sceneJson);
  const rigs = buildCameraRigs(sceneJson.cameras);
  const rigsRoot = new Group();
  rigsRoot.name = "cameras";
  for (const r of rigs) rigsRoot.add(r.group);
  scene.add(rigsRoot);

  // Optional: Gaussian Splat backdrop for free-look. Construction is
  // lazy — the splat file (often 100s of MB) won't be fetched until the
  // user enters free-look at least once.
  const splatBackdrop = makeSplatBackdrop(sceneJson, renderer, scene);

  const skeletons = new SkeletonGroups(
    scene, sceneJson.skeleton_edges, sceneJson.metres_per_wu,
    posesJson.num_keypoints, sceneJson.up_rotation,
  );
  // Keep the Line2 bone shader in pixel sync with the canvas size.
  layoutHooks.push((w, h) => skeletons.setResolution(w, h));
  skeletons.setResolution(canvas.clientWidth || window.innerWidth,
                          canvas.clientHeight || window.innerHeight);
  const poseInterp = new PoseInterpolator(posesJson);

  // --- bone-length gate UI -----------------------------------------
  // Per-bone ratio over expected anatomical length (see skeletonPriors.ts):
  // 1.0 = strict "no bone longer than normal", 1.8 default leaves room for
  // natural stretching + foreshortening/calibration slack, 5.0 ~ shows everything.
  const boneGateInput = document.getElementById("bone-gate") as HTMLInputElement;
  const boneGateRead = document.getElementById("bone-gate-readout") as HTMLSpanElement;
  function applyBoneGate(): void {
    const r = Number(boneGateInput.value);
    skeletons.maxBoneRatio = r;
    boneGateRead.textContent = `${r.toFixed(2)}×`;   // "1.60×"
  }
  boneGateInput.addEventListener("input", applyBoneGate);
  applyBoneGate();

  // --- bone-width slider -------------------------------------------
  const boneWidthInput = document.getElementById("bone-width") as HTMLInputElement;
  const boneWidthRead = document.getElementById("bone-width-readout") as HTMLSpanElement;
  function applyBoneWidth(): void {
    const px = Number(boneWidthInput.value);
    skeletons.setLinewidth(px);
    boneWidthRead.textContent = `${px}px`;
  }
  boneWidthInput.addEventListener("input", applyBoneWidth);
  applyBoneWidth();

  // --- joint-size slider -------------------------------------------
  // Joint dot radius in metres. Lower it so the dense COCO-WholeBody face/hand
  // keypoints stay distinct instead of merging into a blob.
  const jointSizeInput = document.getElementById("joint-size") as HTMLInputElement;
  const jointSizeRead = document.getElementById("joint-size-readout") as HTMLSpanElement;
  function applyJointSize(): void {
    const m = Number(jointSizeInput.value);
    skeletons.setJointSize(m);
    jointSizeRead.textContent = `${m.toFixed(3)}m`;
  }
  jointSizeInput.addEventListener("input", applyJointSize);
  applyJointSize();

  // --- per-gid disable UI ------------------------------------------
  // Mutates skeletons.disabledGids in place; the rAF loop's next
  // applyFrame() skips any gid in that set.
  setupGidPicker(posesJson, skeletons.disabledGids);

  // --- splat-backdrop tuner (only if scene.splat is configured) ---
  if (splatBackdrop) {
    setupSplatTuner(sceneJson, splatBackdrop);
  }

  // --- playback wiring ---------------------------------------------
  const clock = new VideoClock(videoEl);
  const timeline = setupTimeline(clock);

  // --- view modes (free-look vs snap) ------------------------------
  const free = buildFreeLook(canvas, sceneJson);
  const rafState = { activeCamera: free.camera, onTick: (t: number) => {
    timeline.setCurrent(t);
  }};
  type Mode = { kind: "free" } | { kind: "snap"; cam: string };
  let mode: Mode = { kind: "free" };

  // Stash whatever the buildScene() helper put on scene.background so we can
  // restore it when leaving snap mode (snap mode nulls it so the underlying
  // <video> shows through the transparent canvas).
  const defaultSceneBg = scene.background;

  function setMode(next: Mode): void {
    mode = next;
    if (next.kind === "free") {
      videoEl.style.display = "none";
      renderer.setClearAlpha(1.0);
      scene.background = defaultSceneBg;
      rigsRoot.visible = true;
      splatBackdrop?.setVisible(true);   // lazy-loads on first entry
      rafState.activeCamera = free.camera;
      free.controls.enabled = true;
      layoutFor(null);
      free.resize();
      picker.setActive(null);
    } else {
      const calib = sceneJson.cameras.find(c => c.id === next.cam);
      if (!calib) return;
      const v = manifest.videos.find(v => v.cam === next.cam);
      if (!v) return;
      videoEl.style.display = "block";  // overrides the CSS default of `none`
      renderer.setClearAlpha(0.0);       // let backdrop show through
      scene.background = null;       // ditto — three.js's clear must be transparent
      rigsRoot.visible = false;     // don't draw our own frustum on the active cam
      splatBackdrop?.setVisible(false);  // skip splat overdraw when mp4 is the backdrop
      free.controls.enabled = false;
      // Letterbox both <canvas> and <video> to the camera's aspect.
      const liveAspect = layoutFor(calib.aspect);
      const snap = buildSnapCamera(calib);
      snap.aspect = liveAspect;
      snap.updateProjectionMatrix();
      rafState.activeCamera = snap;
      void clock.switchCamera(v.url);
      picker.setActive(next.cam);
    }
    for (const r of rigs) r.setHighlighted(mode.kind === "snap" && mode.cam === r.id);
  }

  // --- camera picker -----------------------------------------------
  const picker = setupCameraPicker(
    manifest,
    (camId) => setMode({ kind: "snap", cam: camId }),
    () => setMode({ kind: "free" }),
  );

  // --- bootstrap a starting camera so the master clock has a src --
  const startCam: ManifestVideo | undefined =
    manifest.videos.find(v => v.cam === manifest.default_camera)
    ?? manifest.videos[0];
  if (startCam) {
    await clock.switchCamera(startCam.url);
    timeline.setDuration(Math.min(clock.duration || 0, poseInterp.duration || Infinity));
    // Start in free-look so the user sees the whole rig + skeletons.
    setMode({ kind: "free" });
  }

  // --- responsive --------------------------------------------------
  window.addEventListener("resize", () => {
    const m = mode;
    if (m.kind === "free") {
      layoutFor(null);
      free.resize();
    } else {
      const calib = sceneJson.cameras.find(c => c.id === m.cam);
      if (calib) {
        const liveAspect = layoutFor(calib.aspect);
        const pc = rafState.activeCamera as import("three").PerspectiveCamera;
        pc.aspect = liveAspect;
        pc.updateProjectionMatrix();
      }
    }
  });

  // The rAF loop reads `rafState.activeCamera` every tick, so mode swaps
  // (which mutate rafState.activeCamera) take effect on the next frame.
  rafState.onTick = (t) => {
    if (mode.kind === "free") free.controls.update();
    timeline.setCurrent(t);
  };
  startRafLoop(renderer, scene, rafState, clock, poseInterp, skeletons);
}

main().catch((e: unknown) => {
  console.error(e);
  status("error: " + (e instanceof Error ? e.message : String(e)));
});
